import {
  NotificationChannel,
  NotificationSettings,
  NotificationType
} from '@/@types/Notifications';
import { toast } from '@/hooks/use-toast';
import { NotificationsService } from '@/services/notifications.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseNotificationSettingsProps {
  onClose: () => void;
}

export const useNotificationSettings = ({ onClose }: UseNotificationSettingsProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const queryClient = useQueryClient();

  const [localSettings, setLocalSettings] = useState<NotificationSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const {
    data: settingsResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: NotificationsService.getSettings,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (settingsResponse?.data) {
      setLocalSettings(settingsResponse.data);
      setHasChanges(false);
    }
  }, [settingsResponse]);

  const { mutate: updateGlobal, isPending: isUpdatingGlobal } = useMutation({
    mutationFn: NotificationsService.updateGlobalSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast({ description: t('common:data_saved'), variant: 'success' });
    },
    onError: () => {
      toast({ description: t('common:data_submission_error'), variant: 'error' });
    },
  });

  const { mutate: bulkUpdate, isPending: isBulkUpdating } = useMutation({
    mutationFn: NotificationsService.bulkUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast({ description: t('common:data_saved'), variant: 'success' });
      setHasChanges(false);
      onClose();
    },
    onError: () => {
      toast({ description: t('common:data_submission_error'), variant: 'error' });
    },
  });

  const toggleGlobalEnabled = useCallback(() => {
    if (!localSettings) return;

    setLocalSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        enabled: !prev.enabled,
      };
    });
    setHasChanges(true);
  }, [localSettings]);

  const toggleDefaultChannel = useCallback((channel: NotificationChannel) => {
    if (!localSettings) return;

    setLocalSettings((prev) => {
      if (!prev) return prev;
      const hasChannel = prev.default_channels.includes(channel);
      const newChannels = hasChannel
        ? prev.default_channels.filter((c) => c !== channel)
        : [...prev.default_channels, channel];

      // Ensure at least one channel is always selected
      if (newChannels.length === 0) return prev;

      return {
        ...prev,
        default_channels: newChannels,
      };
    });
    setHasChanges(true);
  }, [localSettings]);

  const togglePreferenceEnabled = useCallback((type: NotificationType) => {
    if (!localSettings) return;

    setLocalSettings((prev) => {
      if (!prev) return prev;
      const preference = prev.preferences[type];
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [type]: {
            ...preference,
            enabled: !preference.enabled,
            channels: !preference.enabled ? prev.default_channels : [],
          },
        },
      };
    });
    setHasChanges(true);
  }, [localSettings]);

  const togglePreferenceChannel = useCallback(
    (type: NotificationType, channel: NotificationChannel) => {
      if (!localSettings) return;

      setLocalSettings((prev) => {
        if (!prev) return prev;
        const preference = prev.preferences[type];
        const hasChannel = preference.channels.includes(channel);
        const newChannels = hasChannel
          ? preference.channels.filter((c) => c !== channel)
          : [...preference.channels, channel];

        // Ensure at least one channel is always selected when enabled
        if (newChannels.length === 0 && preference.enabled) return prev;

        return {
          ...prev,
          preferences: {
            ...prev.preferences,
            [type]: {
              ...preference,
              channels: newChannels,
            },
          },
        };
      });
      setHasChanges(true);
    },
    [localSettings]
  );

  const setCustomEmail = useCallback((type: NotificationType, email: string | null) => {
    if (!localSettings) return;

    setLocalSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [type]: {
            ...prev.preferences[type],
            custom_email: email,
          },
        },
      };
    });
    setHasChanges(true);
  }, [localSettings]);

  const saveChanges = useCallback(() => {
    if (!localSettings || !hasChanges) return;

    const notifications = Object.entries(localSettings.preferences).map(
      ([type, pref]) => ({
        type: type as NotificationType,
        enabled: pref.enabled,
        channels: pref.channels,
        custom_email: pref.custom_email,
      })
    );

    bulkUpdate({ notifications });
  }, [localSettings, hasChanges, bulkUpdate]);

  const resetChanges = useCallback(() => {
    if (settingsResponse?.data) {
      setLocalSettings(settingsResponse.data);
      setHasChanges(false);
    }
  }, [settingsResponse]);

  return {
    settings: localSettings,
    isLoading,
    isError,
    isPending: isUpdatingGlobal || isBulkUpdating,
    hasChanges,
    toggleGlobalEnabled,
    toggleDefaultChannel,
    togglePreferenceEnabled,
    togglePreferenceChannel,
    setCustomEmail,
    saveChanges,
    resetChanges,
  };
};
